import { Button, HStack, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { FullModal } from '$/components/common/Modals/Modal';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { useToast } from '$/hooks/useToast';
import { type User } from '$/services/usecases/authentication/mapper/genericCloudResponse';
import { deleteTeacher } from '$/services/usecases/school/schools';

interface Props {
  teacher: User;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteTeacherModal: FC<Props> = ({ teacher, isOpen, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const { mutate: removeTeacher, isPending } = useMutation({
    mutationFn: () =>
      deleteTeacher({ schoolId: teacher.schoolId!, teacherId: teacher.id }),
    onSuccess: (data) => {
      if (!data.isSuccessful) {
        toast(cloudMessageTranslation(data.response.message), 'error', '');
        return;
      }
      toast(t('school.teachers.deleteTeacherSuccess'), 'success', '');
      onClose();
    },
  });

  return (
    <FullModal
      isOpen={isOpen}
      onClose={onClose}
      header={t('school.teachers.deleteTeacherModal.title')}
      size='xl'
      isScrollable={false}
    >
      <Stack gap='6'>
        <Text>
          {t('school.teachers.deleteTeacherModal.description', {
            email: teacher.email,
          })}
        </Text>
        <HStack justify='space-between'>
          <Button isDisabled={isPending} onClick={onClose} variant='ghost'>
            {t('general.cancel')}
          </Button>
          <Button
            isLoading={isPending}
            onClick={() => removeTeacher()}
            variant='danger'
          >
            {t('general.delete')}
          </Button>
        </HStack>
      </Stack>
    </FullModal>
  );
};
